import React from 'react'

import { DeliveryProvider } from 'src/apollo/onlineOrdering'
import { useIsIntlRestaurant } from 'src/lib/js/hooks/useIsIntlRestaurant'
import { t, Trans } from 'src/translations'

import {
  CAInfo,
  ToastTOSAndPrivacy
} from 'public/components/default_template/online_ordering/account/pwlessAuth/legalUtils'
import { useCart } from 'public/components/online_ordering/CartContext'
import { useMarketing } from 'public/components/online_ordering/MarketingContext'
import { PaymentOption } from 'public/components/online_ordering/PaymentContext'
import { Cart } from 'public/components/online_ordering/types'

const CheckoutNote = ({
  showIntlPayment,
  paymentOption
}: {
  showIntlPayment?: boolean
  paymentOption?: PaymentOption | null
}) => {
  const { smsAccountEnabled } = useMarketing()
  const international = useIsIntlRestaurant()

  if (international) {
    const payUponReceipt = paymentOption === PaymentOption.UponReceipt
    const openingText =
      showIntlPayment || payUponReceipt
        ? t('checkout-note.opening-text-1')
        : t('checkout-note.opening-text-2')
    return (
      <p
        data-testid='intlCheckoutLegalStatement'
        className='note checkoutNote'
        role='contentinfo'
      >
        <Trans
          i18nKey='checkout-note.intl-checkout-legal-statement'
          components={{ ToastTOSAndPrivacy: <ToastTOSAndPrivacy /> }}
          values={{ openingText }}
        />
      </p>
    )
  }

  // SMS is not available for international restaurants
  if (smsAccountEnabled) {
    return (
      <p
        data-testid='checkoutLegalStatement'
        className='note checkoutNote'
        role='contentinfo'
      >
        <Trans
          i18nKey='checkout-note.checkout-legal-statement-with-marketing'
          components={{
            ToastTOSAndPrivacy: <ToastTOSAndPrivacy />,
            CAInfo: <CAInfo />
          }}
        />
      </p>
    )
  }

  return (
    <p
      data-testid='checkoutLegalStatement'
      className='note checkoutNote'
      role='contentinfo'
    >
      <Trans
        i18nKey='checkout-note.checkout-legal-statement'
        components={{
          ToastTOSAndPrivacy: <ToastTOSAndPrivacy />,
          CAInfo: <CAInfo />
        }}
      />
    </p>
  )
}

/**
 * Returns whether the confirmation page should render the uber disclaimer. Currently TDS restaurants will
 * only have one enabled provider, so checking if the Uber config is enabled is sufficient
 */
const showUberDisclaimer = (cart: Cart | null | undefined) =>
  cart?.deliveryProviderInfo?.provider ===
    DeliveryProvider.ToastDeliveryServices &&
  cart?.restaurant?.deliveryProviders.find(
    ({ provider, enabled }) => enabled && provider === DeliveryProvider.Uber
  )

export const UberCheckoutNote = () => {
  const { cart } = useCart()

  if (!showUberDisclaimer(cart)) {
    return null
  }

  return (
    <p
      data-testid='checkout-uber-disclaimer'
      className='note checkoutNote'
      role='contentinfo'
    >
      <Trans
        i18nKey='checkout-note.uber-disclaimer'
        components={[
          <a
            key='1'
            href='https://www.uber.com/legal/en/document/?name=privacy-notice&country=united-states&lang=en'
            rel='noopener noreferrer'
            target='_blank'
          />
        ]}
      />
    </p>
  )
}

export default CheckoutNote
